import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip,
  FormControlLabel, Checkbox, Alert, LinearProgress, Divider, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  HealthAndSafety as HealthIcon,
  CheckCircle as DoneIcon,
  ErrorOutlined as FailIcon,
  Sync as RunningIcon,
} from '@mui/icons-material';
import {
  previewDomainCheck, startDomainCheckRun, getDomainCheckRun, listDomainCheckRuns,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';

import { LEAD_STATUS_OPTIONS } from '../constants/leadStatus';
const STATUSES = LEAD_STATUS_OPTIONS.map(o => o.value);
const SOURCES = ['ManualEntry', 'CsvImport', 'Google', 'LinkedIn', 'CrunchBase', 'Owler', 'Referral', 'Other', 'AiSearch'];
const HAS_WEBSITE = [
  { value: 'true', label: 'With website only' },
  { value: 'false', label: 'Without website only' },
  { value: '', label: 'Any' },
];

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const defaultFilter = {
  sources: ['CsvImport'],
  statuses: ['New'],
  country: '',
  hasWebsite: 'true',
  includeAlreadyChecked: false,
  createdAfter: '',
  createdBefore: '',
  limit: '',
};

function buildPayload(f) {
  return {
    sources: f.sources && f.sources.length ? f.sources : null,
    statuses: f.statuses && f.statuses.length ? f.statuses : null,
    country: f.country?.trim() || null,
    hasWebsite: f.hasWebsite === '' ? null : f.hasWebsite === 'true',
    includeAlreadyChecked: !!f.includeAlreadyChecked,
    createdAfter: f.createdAfter ? new Date(f.createdAfter).toISOString() : null,
    createdBefore: f.createdBefore ? new Date(f.createdBefore).toISOString() : null,
    limit: f.limit ? parseInt(f.limit, 10) : null,
  };
}

function fmtDuration(start, end) {
  if (!start) return '—';
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const s = Math.max(0, Math.round((b - a) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

export default function DomainCheckPage() {
  const dialog = useDialog();
  const [filter, setFilter] = useState(defaultFilter);
  const [preview, setPreview] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState({ preview: false, start: false });
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await listDomainCheckRuns(20);
      setRecentRuns(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Live poll active job every 2s while Pending/Running.
  useEffect(() => {
    if (!activeJobId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await getDomainCheckRun(activeJobId);
        if (cancelled) return;
        setActiveJob(data);
        if (data && (data.status === 'Completed' || data.status === 'Failed' || data.status === 'Cancelled')) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          loadRecent();
        }
      } catch { /* swallow transient */ }
    };

    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [activeJobId, loadRecent]);

  const handlePreview = async () => {
    setLoading((s) => ({ ...s, preview: true }));
    setError('');
    try {
      const { data } = await previewDomainCheck(buildPayload(filter));
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Preview failed');
    } finally {
      setLoading((s) => ({ ...s, preview: false }));
    }
  };

  const handleRun = async () => {
    const count = preview?.matchCount;
    const msg = count != null
      ? `Run domain check on ${count} matching leads? Broken domains will be marked Disqualified.`
      : 'Run domain check on all matching leads (no preview taken)?';
    if (!(await dialog.confirm({ title: 'Run domain check', message: msg, confirmText: 'Run' }))) return;

    setLoading((s) => ({ ...s, start: true }));
    setError('');
    try {
      const { data } = await startDomainCheckRun(buildPayload(filter));
      setActiveJobId(data.jobId);
      setActiveJob(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to start run');
    } finally {
      setLoading((s) => ({ ...s, start: false }));
    }
  };

  const toggleArr = (key, val) => {
    setFilter((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthIcon color="primary" /> Domain Health Check
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Probe lead websites in batch. Disqualifies unreachable, parked, expired, or empty domains.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filter card */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569' }}>Filter leads</Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Source</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {SOURCES.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                onClick={() => toggleArr('sources', s)}
                color={filter.sources.includes(s) ? 'primary' : 'default'}
                variant={filter.sources.includes(s) ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Status</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {STATUSES.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                onClick={() => toggleArr('statuses', s)}
                color={filter.statuses.includes(s) ? 'primary' : 'default'}
                variant={filter.statuses.includes(s) ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Country (name or code)"
            size="small"
            value={filter.country}
            onChange={(e) => setFilter((f) => ({ ...f, country: e.target.value }))}
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="Has website"
            size="small"
            select
            value={filter.hasWebsite}
            onChange={(e) => setFilter((f) => ({ ...f, hasWebsite: e.target.value }))}
            sx={{ minWidth: 200 }}
          >
            {HAS_WEBSITE.map((o) => <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Created after"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filter.createdAfter}
            onChange={(e) => setFilter((f) => ({ ...f, createdAfter: e.target.value }))}
          />
          <TextField
            label="Created before"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filter.createdBefore}
            onChange={(e) => setFilter((f) => ({ ...f, createdBefore: e.target.value }))}
          />
          <TextField
            label="Limit"
            size="small"
            type="number"
            inputProps={{ min: 1, max: 5000 }}
            value={filter.limit}
            onChange={(e) => setFilter((f) => ({ ...f, limit: e.target.value }))}
            sx={{ maxWidth: 120 }}
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filter.includeAlreadyChecked}
              onChange={(e) => setFilter((f) => ({ ...f, includeAlreadyChecked: e.target.checked }))}
            />
          }
          label={<Typography variant="body2">Re-check leads that were already checked</Typography>}
        />

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={loading.preview}
          >
            {loading.preview ? 'Counting…' : 'Preview match'}
          </Button>
          <Tooltip title="Starts a background job. Backend processes leads in parallel (default 10 concurrent).">
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayIcon />}
                onClick={handleRun}
                disabled={loading.start || (activeJob && (activeJob.status === 'Pending' || activeJob.status === 'Running'))}
              >
                {loading.start ? 'Starting…' : 'Run check'}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            onClick={() => { setFilter(defaultFilter); setPreview(null); }}
          >
            Reset
          </Button>
        </Stack>
      </Paper>

      {/* Preview result */}
      {preview && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>
            Preview: <strong>{preview.matchCount}</strong> matching leads (showing up to 50)
          </Typography>
          {preview.sample && preview.sample.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Website</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Last checked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.sample.map((l) => (
                    <TableRow key={l.id} hover>
                      <TableCell>{l.id}</TableCell>
                      <TableCell>{l.companyName}</TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.website || <em style={{ color: '#94a3b8' }}>—</em>}
                      </TableCell>
                      <TableCell>{l.status}</TableCell>
                      <TableCell>{l.source}</TableCell>
                      <TableCell>{l.country || '—'}</TableCell>
                      <TableCell>{l.domainCheckedAt ? new Date(l.domainCheckedAt).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>No leads match the filter.</Typography>
          )}
        </Paper>
      )}

      {/* Active job */}
      {activeJob && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2, borderColor: activeJob.status === 'Running' ? '#3b82f6' : undefined }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Job #{activeJob.id}
            </Typography>
            <Chip
              size="small"
              label={activeJob.status}
              color={STATUS_CHIP_COLOR[activeJob.status] || 'default'}
              icon={
                activeJob.status === 'Running' ? <RunningIcon fontSize="small" /> :
                activeJob.status === 'Completed' ? <DoneIcon fontSize="small" /> :
                activeJob.status === 'Failed' ? <FailIcon fontSize="small" /> : undefined
              }
            />
            <Typography variant="caption" sx={{ color: '#64748b', ml: 'auto' }}>
              Duration {fmtDuration(activeJob.startedAt, activeJob.finishedAt)}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, activeJob.progressPercent ?? 0)}
            sx={{ mb: 1, height: 8, borderRadius: 1 }}
          />
          <Stack direction="row" spacing={3} sx={{ color: '#475569' }}>
            <Typography variant="body2">Total: <strong>{activeJob.total}</strong></Typography>
            <Typography variant="body2">Processed: <strong>{activeJob.processed}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#dc2626' }}>Disqualified: <strong>{activeJob.disqualified}</strong></Typography>
            <Typography variant="body2">Errors: <strong>{activeJob.errors}</strong></Typography>
          </Stack>
          {activeJob.errorMessage && (
            <Alert severity="error" sx={{ mt: 1.5 }}>{activeJob.errorMessage}</Alert>
          )}
        </Paper>
      )}

      {/* Recent runs */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Recent runs</Typography>
          <IconButton size="small" onClick={loadRecent} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Processed</TableCell>
                <TableCell align="right">Disqualified</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell>By</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentRuns.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>#{r.id}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_CHIP_COLOR[r.status] || 'default'} />
                  </TableCell>
                  <TableCell>{r.trigger}</TableCell>
                  <TableCell>{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{fmtDuration(r.startedAt, r.finishedAt)}</TableCell>
                  <TableCell align="right">{r.total}</TableCell>
                  <TableCell align="right">{r.processed}</TableCell>
                  <TableCell align="right" sx={{ color: '#dc2626' }}>{r.disqualified}</TableCell>
                  <TableCell align="right">{r.errors}</TableCell>
                  <TableCell>{r.createdByUsername || '—'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setActiveJobId(r.id)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {recentRuns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ color: '#94a3b8', textAlign: 'center' }}>No runs yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
